import { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { BoosterBoosterCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { Button } from '@ui/button.tsx';
import { Skeleton } from '@ui/skeleton.tsx';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ui/collapsible.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';
import { cn } from '@r/utils/utils.ts';
import { BoosterCard } from './booster-card.tsx';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 30];

interface BoosterCardsSectionProps {
  title: string;
  boosters: BoosterBoosterCollectionReadModel[];
  isLoading?: boolean;
  emptyMessageKey: string;
  showEdit?: boolean;
  showDelete?: boolean;
  onEdit?: (booster: BoosterBoosterCollectionReadModel) => void;
  onDelete?: (booster: BoosterBoosterCollectionReadModel) => void;
  deletingBoosterId?: number | null;
}

function BoosterCardSkeleton() {
  return <Skeleton className="h-72 w-full rounded-xl" />;
}

/** Section repliable de cartes boosters avec pagination locale. */
export function BoosterCardsSection({
  title,
  boosters,
  isLoading = false,
  emptyMessageKey,
  showEdit = false,
  showDelete = false,
  onEdit,
  onDelete,
  deletingBoosterId
}: BoosterCardsSectionProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const pageCount = Math.max(1, Math.ceil(boosters.length / pageSize));

  useEffect(() => {
    setPageIndex(0);
  }, [boosters.length, pageSize]);

  useEffect(() => {
    if (pageIndex > pageCount - 1) {
      setPageIndex(Math.max(0, pageCount - 1));
    }
  }, [pageCount, pageIndex]);

  const paginatedBoosters = useMemo(() => {
    const start = pageIndex * pageSize;
    return boosters.slice(start, start + pageSize);
  }, [boosters, pageIndex, pageSize]);

  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <section className="space-y-4">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 text-left"
            aria-expanded={isOpen}
          >
            <ChevronDown
              className={cn('h-5 w-5 shrink-0 transition-transform', isOpen && 'rotate-180')}
            />
            <h2 className="text-2xl font-bold">{title}</h2>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-4">
          {isLoading && (
            <div className="space-y-4">
              <BoosterCardSkeleton />
            </div>
          )}

          {!isLoading && boosters.length === 0 && (
            <p className="text-sm text-muted-foreground">{t(emptyMessageKey)}</p>
          )}

          {!isLoading && paginatedBoosters.length > 0 && (
            <div className="space-y-4">
              {paginatedBoosters.map((booster) => (
                <BoosterCard
                  key={booster.id}
                  booster={booster}
                  showEdit={showEdit}
                  showDelete={showDelete}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  isDeleting={deletingBoosterId === booster.id}
                />
              ))}
            </div>
          )}

          {!isLoading && boosters.length > 0 && (
            <div className="flex w-full flex-col-reverse items-center justify-end gap-4 overflow-auto pt-2.5 sm:flex-row sm:gap-8">
              <div className="flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8">
                <div className="flex items-center space-x-2">
                  <p className="whitespace-nowrap text-sm font-medium">{t('pagination.limit')}</p>
                  <Select
                    value={`${pageSize}`}
                    onValueChange={(value) => setPageSize(Number(value))}
                  >
                    <SelectTrigger className="h-8 w-[4.5rem]">
                      <SelectValue placeholder={pageSize} />
                    </SelectTrigger>
                    <SelectContent side="top">
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <SelectItem key={option} value={`${option}`}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-center text-sm font-medium">
                  {t('pagination.info', {
                    current: pageIndex + 1,
                    total: pageCount
                  })}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    aria-label="Go to first page"
                    variant="outline"
                    size="custom_icon"
                    className="hidden rounded-md p-0 lg:flex"
                    onClick={() => setPageIndex(0)}
                    disabled={!canPreviousPage}
                  >
                    <ChevronsLeftIcon className="size-4" aria-hidden="true" />
                  </Button>
                  <Button
                    aria-label="Go to previous page"
                    variant="outline"
                    size="custom_icon"
                    className="rounded-md"
                    onClick={() => setPageIndex((value) => value - 1)}
                    disabled={!canPreviousPage}
                  >
                    <ChevronLeftIcon className="size-4" aria-hidden="true" />
                  </Button>
                  <Button
                    aria-label="Go to next page"
                    variant="outline"
                    size="custom_icon"
                    className="rounded-md"
                    onClick={() => setPageIndex((value) => value + 1)}
                    disabled={!canNextPage}
                  >
                    <ChevronRightIcon className="size-4" aria-hidden="true" />
                  </Button>
                  <Button
                    aria-label="Go to last page"
                    variant="outline"
                    size="custom_icon"
                    className="hidden rounded-md p-0 lg:flex"
                    onClick={() => setPageIndex(pageCount - 1)}
                    disabled={!canNextPage}
                  >
                    <ChevronsRightIcon className="size-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}
